/**
 * Email Verification Page - Liquid Glass UI
 * Handles email verification token from URL
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { verifyEmail } from '../services/authService';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await verifyEmail(token);
        setStatus('success');
        setMessage(response.message || 'Email verified successfully!');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage(
          error.response?.data?.detail ||
          'Verification failed. The link may be expired or invalid.'
        );
      }
    };

    verify();
  }, [token, navigate]);

  return (
    <div className="min-h-screen liquid-bg flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="liquid-blob blob-1"></div>
        <div className="liquid-blob blob-2"></div>
        <div className="liquid-blob blob-3"></div>
      </div>

      <div className="relative z-10 glass-panel max-w-md w-full p-8 text-center">
        {/* Logo/Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold gradient-text" style={{ fontFamily: "'Poppins', sans-serif" }}>
            CarbonRoute
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Powered by Arantree Consulting Services
          </p>
        </div>

        {status === 'verifying' && (
          <div className="py-8">
            <div className="w-16 h-16 mx-auto mb-6">
              <div className="w-full h-full rounded-full border-4 border-teal-200 border-t-teal-600 animate-spin"></div>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Verifying your email...</h2>
            <p className="text-gray-500">Please wait a moment</p>
          </div>
        )}

        {status === 'success' && (
          <div className="py-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-6 shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Email Verified!</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <div className="glass-alert glass-alert-success mb-4">
              <p className="text-sm font-medium">
                You can now sign in to start using CarbonRoute!
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Redirecting to login page...
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="py-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center mb-6 shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Verification Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>

            <div className="space-y-3">
              <Link
                to="/login"
                className="glass-button-primary block w-full py-3 text-sm font-semibold text-center"
              >
                Go to Login
              </Link>
              <Link
                to="/signup"
                className="glass-button-secondary block w-full py-3 text-sm font-semibold text-center"
              >
                Create New Account
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
